import React, { useState, useMemo }  from "react";
import { useViewport } from 'components/general/useViewPort'
import Label from "components/general/Label"
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Button from 'react-bootstrap/Button';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Stack from 'react-bootstrap/Stack';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faExpand, faPlus
} from "@fortawesome/free-solid-svg-icons";
import 'styles/sports/glf.scss';

function getInitialCurrentHole(participant, unitData){
  const participantData = unitData?.results?.items?.find(r => r.participant.code === participant);
  const current_hole = participantData?.extendedResults?.find(er => er.er_code === 'CURRENT')?.er_value;
  return (current_hole) 
    ? current_hole
    : "1"
}

const PlayOffShotTracker = ({participant, unitData} ) => {
  const { breakPoint } = useViewport();
  const isMobile = (breakPoint === "sm" || breakPoint === "xs")  

  const [currentHole, setCurrentHole] = useState(getInitialCurrentHole(participant, unitData));
  const [currentMetric, setMetric] = useState("metric")
  const [currentZoom, setZoom] = useState("0") // 0: normal, 1: zoom x2, 3:zoom to pin
  
  return (
    <>
      <ButtonToolbar>
        <ButtonGroupHole 
          currentHole={currentHole} 
          setCurrentHole={setCurrentHole} 
          unitData={unitData} 
          isMobile={isMobile}
          />
        <ButtonGroupMetric 
          currentMetric={currentMetric} 
          setMetric={setMetric}
          />
      </ButtonToolbar>
      <HoleInfo 
        currentMetric={currentMetric} 
        participant={participant} 
        currentHole={currentHole} 
        unitData={unitData} 
        currentZoom={currentZoom} 
        setZoom={setZoom} 
        isMobile={isMobile}
        />
      <ScoreCard 
        currentMetric={currentMetric} 
        participant={participant} 
        unitData={unitData} 
        isMobile={isMobile}
        /> 
    </>
  )
};

export default PlayOffShotTracker;

const HoleInfo = ({currentMetric, participant, currentHole, unitData, currentZoom, setZoom, isMobile} ) => {
  const holeConfig = unitData?.config?.extendedConfig?.find(hole => hole.code === 'HOLE' && hole.pos === currentHole)
  const hole = unitData?.results?.extendedInfos.find(ei => ei.ei_code === 'PLAYOFF' && ei.ei_pos === currentHole)?.ei_value
  const par = holeConfig?.value
  const meters = holeConfig?.extension?.find(ext => ext.code === 'DISTANCE_TEE_BOX' && ext.pos === "0" )?.value
  const yards = holeConfig?.extension?.find(ext => ext.code === 'DISTANCE_TEE_BOX' && ext.pos === "1" )?.value
  return(
    <>
      <Stack className="mt-1 align-items-end" direction={currentZoom === "0" ? "horizontal": "vertical"}>
        <Stack direction="horizontal"  className="align-items-end">
          <HoleImage participant={participant} 
            currentHole={currentHole} 
            unitData={unitData} 
            currentZoom={currentZoom} 
            currentMetric={currentMetric}
            isMobile={isMobile}/>
          <ButtonGroupZoom currentZoom={currentZoom} setZoom={setZoom}/>
        </Stack>
        <Stack direction="vertical" className="mt-1 align-items-start">
          <div className="p-1">
            <strong><Label data="HeaderHole"/> {hole} - <Label data="HeaderPar"/> {par}</strong>
          </div>
          {(currentMetric === 'metric') 
            ? (
              <div className="p-1">
                <strong>{meters} <Label data="HeaderMeters"/></strong>
              </div>
            ) : (
              <div className="p-1">
                <strong>{yards} <Label data="HeaderYards"/></strong>
              </div>
            )
          }	
        </Stack>
      </Stack>
  </>
  )
};

const HoleImage = ({participant, currentHole, unitData, currentZoom, currentMetric, isMobile}) =>{
  const holeExtInfo = unitData?.results?.extendedInfos.find(ei => ei.ei_code === 'PLAYOFF' && ei.ei_pos ===currentHole)
  const currentImage = unitData?.images?.find(img => img.imgPos === holeExtInfo.ei_value)
  const img_url = currentImage?.imagePath
  var image_width = isMobile ? 175 : 375
  var image_height = isMobile ?  82 : 175
  const hole = unitData?.config?.extendedConfig?.find(hole => hole.code === 'HOLE' && hole.pos === currentHole)
  const pin_x_perc = hole?.extension?.find(ext => ext.code === 'PIN_X' && ext.pos === "1" )?.value
  const pin_y_perc = hole?.extension?.find(ext => ext.code === 'PIN_Y' && ext.pos === "1" )?.value
  const tee_x_perc = hole?.extension?.find(ext => ext.code === 'TEE_X' && ext.pos === "1" )?.value
  const tee_y_perc = hole?.extension?.find(ext => ext.code === 'TEE_Y' && ext.pos === "1" )?.value

  var viewbox_x = 0;
  var viewbox_y = 0;
  var viewbox_width = image_width;
  var viewbox_height = image_height;
  var svg_width = image_width;
  var svg_height = image_height;
  
  switch(currentZoom){
    case "1":
      image_width = image_width * 2;
      image_height = image_height *2;
      viewbox_width = image_width;
      viewbox_height = image_height;
      svg_width = image_width;
      svg_height = image_height;
      break;
    case "2":      
      image_width = image_width * 3;
      image_height = image_height * 3;
      viewbox_x = ((pin_x_perc * image_width) / 100)-50;
      viewbox_y = ((pin_y_perc * image_height) / 100)-50;
      svg_width = 350;
      svg_height = 350;      
      viewbox_width = 100;
      viewbox_height = 100;
      break;
  }

  var last_x = (tee_x_perc * image_width) / 100;
  var last_y = (tee_y_perc * image_height) / 100;

  return (
    <svg
      key={`svg-${currentHole}-${participant}`}
      xmlns="http://www.w3.org/2000/svg"
      className="glf-hole-image"
      width={`${svg_width}`}
      height={`${svg_height}`}
      viewBox={`${viewbox_x} ${viewbox_y} ${viewbox_width} ${viewbox_height}`}
      >
      <pattern id={`po-img-hole-${currentHole}-${participant}`} patternUnits="userSpaceOnUse" x="0" y="0" width={`${image_width}`} height={`${image_height}`}>
        <image href={`${process.env.REACT_APP_SERVER_URL_RESOURCES}/img/${img_url}`}  width={`${image_width}`} height={`${image_height}`} />             
      </pattern>				        
      <marker 
        id={`po-dot-${currentHole}-${participant}`}      
        viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" 
        className="glf-dot-marker">
        <circle cx="5" cy="5" r="5" vectorEffect="non-scaling-size"/>
      </marker>              
      <symbol 
        id={`po-pin-${currentHole}-${participant}`}
        className="glf-pin-marker" >
          <path d="m8.9935,27.21373c-7.55305,-11.32949 -8.95504,-12.49224 -8.95504,-16.656c0,-5.70344 4.46852,-10.32697 9.98077,-10.32697s9.98077,4.62352 9.98077,10.32697c0,4.16376 -1.40199,5.32651 -8.95504,16.656c-0.49566,0.74085 -1.55586,0.7408 -2.05146,0z"/>
      </symbol>	              
      <path 
        d={`M0,0 l0,${image_height} l${image_width},0 l0,-${image_height} l-${image_width},0`} 
        fill={`url(#po-img-hole-${currentHole}-${participant})`}
        />
      {
        unitData?.results?.playByPlay
          ?.find(pbp => pbp?.actions.find(a=> a.pbpa_period===`${participant}HOLE_${currentHole.padStart(2, '0')}`))
          ?.actions
          .sort(({ pbpa_order: prevOrder }, { pbpa_order: currOrder }) => prevOrder - currOrder)
          .map(action =>{
            const shot = action.pbpa_order;
            const coord_x_perc = action.pbpa_X;
            const coord_y_perc = action.pbpa_Y;
            const coord_x = (coord_x_perc * image_width) / 100;
            const coord_y = (coord_y_perc * image_height) / 100;
            const shot_distance_m = action?.extendedActions.find(ea => ea.pbpea_code === 'SHOT' && ea.pbpea_pos === '0')?.pbpea_value;
            const shot_distance_y = action?.extendedActions.find(ea => ea.pbpea_code === 'SHOT' && ea.pbpea_pos === '1')?.pbpea_value;
            const dtp_m = action?.extendedActions.find(ea => ea.pbpea_code === 'DTP' && ea.pbpea_pos === '0')?.pbpea_value;
            const dtp_y = action?.extendedActions.find(ea => ea.pbpea_code === 'DTP' && ea.pbpea_pos === '1')?.pbpea_value;
            const origin_x = last_x;
            const origin_y = last_y;
            last_x = coord_x;
            last_y = coord_y;

            return(
              <>
              <line 
                key={`line-${currentHole}-${participant})-${shot}`}
                x1={origin_x} y1={origin_y} 
                x2={coord_x} y2={coord_y}
                className="glf-line" 
                markerStart={`url(#po-dot-${currentHole}-${participant})`} 
                markerEnd={`url(#po-dot-${currentHole}-${participant})`}  
                vectorEffect="non-scaling-stroke"/>
              <g transform={`translate(${coord_x}, ${coord_y})`} viewBox="0 0 10 14" width="20" height="28">
                <use xlinkHref={`#po-pin-${currentHole}-${participant}`} x="-10" y="-31"/>			     	
                <text className="glf-pin-text">  
                  <tspan x="-3" y="-14">{shot}</tspan>
                </text>
                <title>            
                    <Label data="LabelShotLength"/>&nbsp;
                    {(currentMetric === 'metric') 
                      ? (<>{shot_distance_m}&nbsp;<Label data="HeaderMeters"/></>)
                      : (<>{shot_distance_y}&nbsp;<Label data="HeaderYards"/></>)
                    }
                    {'\n'}                 
                    <Label data="LabelDistanceToPin"/>&nbsp;
                    {(currentMetric === 'metric') 
                      ? (<>{dtp_m}&nbsp;<Label data="HeaderMeters"/></>)
                      : (<>{dtp_y}&nbsp;<Label data="HeaderYards"/></>)
                    }                 
                </title>
              </g>
              </>
            )
          })
      }        
  </svg>
  )
}

const ButtonGroupMetric = ({ currentMetric, setMetric }) => {
  const types = [
    { type: 'metric', label: <Label data="HeaderMetric" /> }, 
    { type: 'imperial', label: <Label data="HeaderImperial" /> }
  ];

  const handleClick = (e) => {
      setMetric(e.target.value)
  }
  return (
      <ButtonGroup size="sm" className="align-items-end" onClick={handleClick}>
          {types.map((obj, index) => (
              <Button key={`btn-measureunit-${index}`} className="selector" variant="outline-dark" active={currentMetric === obj.type} value={obj.type} >{obj.label}</Button>
          ))}
      </ButtonGroup>
  );
}

const ButtonGroupHole = ({ currentHole, setCurrentHole, unitData }) => {
  const handleClick = (e) => {
    setCurrentHole(e.target.value)
  }
  return (
    <>
      <Stack direction="vertical" className="align-items-start">
        <div className="text-left"><strong><Label data="HeaderHole"/></strong></div>
        <div>
        <ButtonGroup size="sm" className="me-2" aria-label="Hole" >
          {
              unitData?.results?.extendedInfos.filter(ei => ei.ei_code === 'PLAYOFF').map((hole) => {
                return(
                  <Button key={`btn-hole-${hole.ei_pos}`} active={currentHole===hole.ei_pos} variant="outline-dark" onClick={handleClick} value={hole.ei_pos}>{hole.ei_value}</Button>
                )
              })
          }          
        </ButtonGroup>
        </div>
      </Stack>
    </>
  );
}

const ButtonGroupZoom = ({currentZoom, setZoom}) => {
  const handleClickZoom = (e) => {
    const value = currentZoom === "0" 
      ? e.currentTarget.value 
      : currentZoom === e.currentTarget.value 
        ? "0"
        : e.currentTarget.value;
    setZoom(value)
  }
  return (
    <ButtonGroup size="sm" vertical className="align-items-end"  >
      <Button variant="outline-dark" active={currentZoom === "1"} value="1" onClick={handleClickZoom}>
        <FontAwesomeIcon className="font-icon" icon={faExpand} />
      </Button>
      <Button variant="outline-dark" active={currentZoom === "2"} value="2" onClick={handleClickZoom}>
        <FontAwesomeIcon className="font-icon" icon={faPlus} />
      </Button>
    </ButtonGroup>    
  )
}

const ScoreCard = ({ currentMetric, participant, unitData }) => {
  let extendedResults = unitData?.results?.items.find(i => i?.participant?.code === participant)?.extendedResults || []
  const current = extendedResults.find(er => er.er_code === 'CURRENT')?.er_value || ""

  let holesInfo = unitData?.results?.extendedInfos.filter(ei => ei.ei_code === 'PLAYOFF').sort(({ ei_pos: prevOrder }, { ei_pos: currOrder }) => prevOrder - currOrder)

  return(
    <>
    {holesInfo 
      ? 
      <table className="table table-detail table-sm">
        <thead>
          <tr>
            <th className="text-start"><Label data="HeaderHole"/></th>
            {
              holesInfo.map(ei =>{
                  return <th key={`scorecard-1-${ei.ei_pos}`} className={`text-center${current===ei.ei_pos ? " score-current" : ""}`}>{ei.ei_value}</th>
              })
            }
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="text-start"><Label data="HeaderScore"/></td>
            {holesInfo.map(ei =>{
                const result = extendedResults.find(er => er.er_code === 'HOLE' && er.er_pos === ei.ei_pos)?.er_value 
                return <td key={`scorecard-2-${ei.ei_pos}`} className={`text-center${current===ei.ei_pos ? " score-current" : ""}`}>{result}</td>
            })
          }
          </tr>          
          <tr>
          <td className="text-start"><Label data="HeaderPar"/></td>
            {holesInfo.map(ei =>{
                const par = unitData?.config?.extendedConfig.find(c => c.code === 'HOLE' && c.pos === ei.ei_pos)?.value
                return <td key={`scorecard-3-${ei.ei_pos}`} className={`text-center${current===ei.ei_pos ? " score-current" : ""}`}>{par}</td>
            })
          }
          </tr>          
          <tr>
            <td className="text-start"><Label data="HeaderMeters"/></td>
            {holesInfo.map(ei =>{
                const pos = currentMetric === 'metric' ? "0" : "1"
                const distance = unitData?.config?.extendedConfig
                  .find(c => c.code === 'HOLE' && c.pos === ei.ei_pos)?.extension
                  .find(e => e.code === 'DISTANCE_TEE_BOX' && e.pos === pos)?.value
                return <td key={`scorecard-4-${ei.ei_pos}`} className={`text-center${current===ei.ei_pos ? " score-current" : ""}`}>{distance}</td>
            })
          }
          </tr>          
        </tbody>
      </table>
      : ''
    }
    </>
  )
}